import { useState } from 'react';

import ImageLightbox from './ImageLightbox';

interface Media {
  type: 'image' | 'video';
  url: string;
}

interface PostMediaGridProps {
  media: Media[];
}

export default function PostMediaGrid({ media }: PostMediaGridProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const images = media.filter((m) => m.type === 'image');
  if (images.length === 0) return null;

  const openLightbox = (idx: number) => setLightboxIndex(idx);

  // Facebook-style grid layouts
  if (images.length === 1) {
    return (
      <>
        <div className="mt-3 -mx-4">
          <img
            src={images[0].url}
            alt=""
            className="w-full max-h-[480px] object-cover cursor-pointer"
            onClick={() => openLightbox(0)}
          />
        </div>
        {lightboxIndex !== null && (
          <ImageLightbox
            images={images}
            initialIndex={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
          />
        )}
      </>
    );
  }

  if (images.length === 2) {
    return (
      <>
        <div className="mt-3 -mx-4 grid grid-cols-2 gap-0.5">
          {images.map((img, i) => (
            <img
              key={i}
              src={img.url}
              alt=""
              className="w-full h-[280px] object-cover cursor-pointer"
              onClick={() => openLightbox(i)}
            />
          ))}
        </div>
        {lightboxIndex !== null && (
          <ImageLightbox
            images={images}
            initialIndex={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
          />
        )}
      </>
    );
  }

  if (images.length === 3) {
    return (
      <>
        <div className="mt-3 -mx-4 grid grid-cols-2 gap-0.5">
          <img
            src={images[0].url}
            alt=""
            className="w-full h-[280px] object-cover cursor-pointer row-span-2"
            onClick={() => openLightbox(0)}
          />
          <img
            src={images[1].url}
            alt=""
            className="w-full h-[139px] object-cover cursor-pointer"
            onClick={() => openLightbox(1)}
          />
          <img
            src={images[2].url}
            alt=""
            className="w-full h-[139px] object-cover cursor-pointer"
            onClick={() => openLightbox(2)}
          />
        </div>
        {lightboxIndex !== null && (
          <ImageLightbox
            images={images}
            initialIndex={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
          />
        )}
      </>
    );
  }

  // 4+ images: 2x2 grid with overflow indicator
  const shown = images.slice(0, 4);
  const remaining = images.length - 4;

  return (
    <>
      <div className="mt-3 -mx-4 grid grid-cols-2 gap-0.5">
        {shown.map((img, i) => (
          <div key={i} className="relative">
            <img
              src={img.url}
              alt=""
              className="w-full h-[180px] object-cover cursor-pointer"
              onClick={() => openLightbox(i)}
            />
            {i === 3 && remaining > 0 && (
              <div
                className="absolute inset-0 bg-black/50 flex items-center justify-center cursor-pointer"
                onClick={() => openLightbox(3)}
              >
                <span className="text-white text-2xl font-bold">+{remaining}</span>
              </div>
            )}
          </div>
        ))}
      </div>
      {lightboxIndex !== null && (
        <ImageLightbox
          images={images}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  );
}
